import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { legalPageStyles as styles } from './LegalPage.styles';
import type { LegalBlock } from './legalContentTypes';

/** Splits `text` around each `links` match, rendering the matches as real
 *  anchors — matches the source site's markup (email/URL mentions are
 *  actual links there, not plain text). */
function renderWithLinks(
  text: string,
  links: { match: string; href: string; bold?: boolean }[] | undefined,
): ReactNode {
  if (!links?.length) return text;
  const found = links
    .map((link) => ({ ...link, index: text.indexOf(link.match) }))
    .filter((link) => link.index !== -1)
    .sort((a, b) => a.index - b.index);
  if (!found.length) return text;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  found.forEach((link, i) => {
    if (link.index > cursor) nodes.push(text.slice(cursor, link.index));
    nodes.push(
      <Link key={i} href={link.href} sx={link.bold ? styles.linkBold : styles.link}>
        {link.match}
      </Link>,
    );
    cursor = link.index + link.match.length;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/** Splits `text` around each `boldTerms` match, wrapping each in a plain
 *  (non-link) bold span — e.g. defined terms bolded on first use. */
function renderWithBoldTerms(text: string, boldTerms: string[] | undefined): ReactNode {
  if (!boldTerms?.length) return text;
  const found = boldTerms
    .map((term) => ({ term, index: text.indexOf(term) }))
    .filter((t) => t.index !== -1)
    .sort((a, b) => a.index - b.index);
  if (!found.length) return text;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  found.forEach((t, i) => {
    if (t.index > cursor) nodes.push(text.slice(cursor, t.index));
    nodes.push(
      <Typography key={i} component="strong" sx={styles.boldLead}>
        {t.term}
      </Typography>,
    );
    cursor = t.index + t.term.length;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/** Renders a Privacy Policy/Terms & Conditions document from structured blocks. */
export function LegalContent({ blocks }: { blocks: LegalBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography key={index} component="h2" sx={styles.heading}>
                {block.text}
              </Typography>
            );
          case 'subheading':
            return (
              <Typography key={index} component="h3" sx={styles.subheading}>
                {block.text}
              </Typography>
            );
          case 'emphasis':
            return (
              <Typography key={index} sx={styles.emphasis}>
                {block.text}
              </Typography>
            );
          case 'list':
            return (
              <Typography key={index} component="ul" sx={styles.list}>
                {block.items.map((item, itemIndex) => (
                  <Typography key={itemIndex} component="li" sx={styles.listItem}>
                    {item}
                  </Typography>
                ))}
              </Typography>
            );
          case 'paragraph':
          default:
            return (
              <Typography key={index} sx={styles.paragraph}>
                {block.boldLead && (
                  <Typography component="strong" sx={styles.boldLead}>
                    {block.boldLead}
                  </Typography>
                )}
                {block.boldLead ? (block.leadSeparator ?? ': ') : null}
                {block.links
                  ? renderWithLinks(block.text, block.links)
                  : renderWithBoldTerms(block.text, block.boldTerms)}
              </Typography>
            );
        }
      })}
    </>
  );
}
